import { useEffect, useMemo, useState, useRef } from "react";
import {
	useLoaderData,
	useSearchParams,
	useFetcher,
	useRevalidator,
} from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Trash2, Lock, LockOpen } from "lucide-react";
import {
	testDataViewLoader,
	type TestDataViewActionResult,
} from "@/routes/testDataViewRoute";

type ActionResult = TestDataViewActionResult;

export const TestDataView = () => {
	const { toast } = useToast();
	const data = useLoaderData<typeof testDataViewLoader>();
	const [searchParams, setSearchParams] = useSearchParams();
	const revalidator = useRevalidator();
	const datasetFetcher = useFetcher<ActionResult>();
	const entryFetcher = useFetcher<ActionResult>();
	const deleteFetcher = useFetcher<ActionResult>();

	const [newDatasetName, setNewDatasetName] = useState("");
	const [newDerivativeName, setNewDerivativeName] = useState("");
	const [derivativeParentId, setDerivativeParentId] = useState("");
	const [newEntryInput, setNewEntryInput] = useState("");
	const [newEntryExpected, setNewEntryExpected] = useState("");
	const [selectedParentEntryId, setSelectedParentEntryId] = useState("");

	const handledDatasetResult = useRef<ActionResult | null>(null);
	const handledEntryResult = useRef<ActionResult | null>(null);
	const handledDeleteResult = useRef<ActionResult | null>(null);
	const handledDatasetDeleteResult = useRef<ActionResult | null>(null);

	const deleteDatasetFetcher = useFetcher<ActionResult>();

	const {
		datasets,
		selectedDatasetId,
		selectedDataset,
		entries,
		parentEntries,
		pagination,
		lockState,
		userIsAdmin,
	} = data;
	const isLocked = Boolean(lockState.isLocked);

	const canDeleteDataset =
		selectedDataset &&
		!isLocked &&
		(userIsAdmin ||
			selectedDataset.user_id ===
				data.datasets.find((d) => d.id === selectedDatasetId)?.user_id);

	const primaryDatasets = useMemo(
		() => datasets.filter((dataset) => dataset.type === "primary"),
		[datasets],
	);

	const currentParentEntries =
		selectedDataset?.type === "adversarial" ? parentEntries : [];

	useEffect(() => {
		setSelectedParentEntryId("");
	}, [selectedDatasetId]);

	useEffect(() => {
		if (datasetFetcher.state !== "idle" || !datasetFetcher.data) {
			return;
		}

		if (handledDatasetResult.current === datasetFetcher.data) {
			return;
		}
		handledDatasetResult.current = datasetFetcher.data;

		if (datasetFetcher.data.ok) {
			if (datasetFetcher.data.intent === "createDataset") {
				setNewDatasetName("");
				setNewDerivativeName("");
				setDerivativeParentId("");
				toast({
					title: "Dataset created",
					description: "The dataset is now available.",
				});
				revalidator.revalidate();
				if (datasetFetcher.data.datasetId) {
					const params = new URLSearchParams(searchParams);
					params.set("datasetId", datasetFetcher.data.datasetId);
					params.delete("page");
					setSearchParams(params);
				}
			}
		} else {
			toast({
				title: "Dataset action failed",
				description: datasetFetcher.data.error,
				variant: "destructive",
			});
		}
	}, [
		datasetFetcher.state,
		datasetFetcher.data,
		revalidator,
		toast,
		searchParams,
		setSearchParams,
	]);

	useEffect(() => {
		if (entryFetcher.state !== "idle" || !entryFetcher.data) {
			return;
		}

		if (handledEntryResult.current === entryFetcher.data) {
			return;
		}
		handledEntryResult.current = entryFetcher.data;

		if (entryFetcher.data.ok) {
			setNewEntryInput("");
			setNewEntryExpected("");
			setSelectedParentEntryId("");
			toast({
				title: "Entry added",
				description: "Test data entry created successfully.",
			});
			revalidator.revalidate();
		} else {
			toast({
				title: "Entry creation failed",
				description: entryFetcher.data.error,
				variant: "destructive",
			});
		}
	}, [entryFetcher.state, entryFetcher.data, revalidator, toast]);

	useEffect(() => {
		if (deleteFetcher.state !== "idle" || !deleteFetcher.data) {
			return;
		}

		if (handledDeleteResult.current === deleteFetcher.data) {
			return;
		}
		handledDeleteResult.current = deleteFetcher.data;

		if (deleteFetcher.data.ok) {
			toast({
				title: "Entry deleted",
				description: "The entry has been removed.",
			});
			revalidator.revalidate();
		} else {
			toast({
				title: "Entry deletion failed",
				description: deleteFetcher.data.error,
				variant: "destructive",
			});
		}
	}, [deleteFetcher.state, deleteFetcher.data, revalidator, toast]);

	useEffect(() => {
		if (deleteDatasetFetcher.state !== "idle" || !deleteDatasetFetcher.data) {
			return;
		}

		if (handledDatasetDeleteResult.current === deleteDatasetFetcher.data) {
			return;
		}
		handledDatasetDeleteResult.current = deleteDatasetFetcher.data;

		if (deleteDatasetFetcher.data.ok) {
			toast({
				title: "Dataset deleted",
				description: "The dataset has been permanently deleted.",
			});
			revalidator.revalidate();
			// Clear the dataset selection
			const params = new URLSearchParams(searchParams);
			params.delete("datasetId");
			params.delete("page");
			setSearchParams(params);
		} else {
			toast({
				title: "Dataset deletion failed",
				description: deleteDatasetFetcher.data.error,
				variant: "destructive",
			});
		}
	}, [
		deleteDatasetFetcher.state,
		deleteDatasetFetcher.data,
		revalidator,
		toast,
		searchParams,
		setSearchParams,
	]);

	useEffect(() => {
		if (!selectedDatasetId) {
			return;
		}

		if (!searchParams.get("datasetId")) {
			const params = new URLSearchParams(searchParams);
			params.set("datasetId", selectedDatasetId);
			params.set("page", String(pagination.page));
			setSearchParams(params, { replace: true });
		}
	}, [selectedDatasetId, pagination.page, searchParams, setSearchParams]);

	const handleDatasetChange = (id: string) => {
		const params = new URLSearchParams(searchParams);
		if (id) {
			params.set("datasetId", id);
		} else {
			params.delete("datasetId");
		}
		params.delete("page");
		setSearchParams(params);
	};

	const handlePageChange = (nextPage: number) => {
		const params = new URLSearchParams(searchParams);
		if (selectedDatasetId) {
			params.set("datasetId", selectedDatasetId);
		}
		params.set("page", String(nextPage));
		setSearchParams(params, { replace: true });
	};

	if (datasets.length === 0) {
		return (
			<div className="p-8 space-y-6">
				<Card>
					<CardHeader>
						<CardTitle>Create your first test dataset</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-sm text-muted-foreground">
							Test datasets help you validate agents deterministically. Create a
							primary dataset to get started.
						</p>
						<datasetFetcher.Form
							method="post"
							className="flex gap-2"
							action="/workspace/test-data"
						>
							<input type="hidden" name="intent" value="createDataset" />
							<Input
								name="name"
								placeholder="Dataset name"
								value={newDatasetName}
								onChange={(event) => setNewDatasetName(event.target.value)}
							/>
							<Button
								type="submit"
								disabled={
									!newDatasetName.trim() || datasetFetcher.state !== "idle"
								}
							>
								{datasetFetcher.state !== "idle" ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									"Create dataset"
								)}
							</Button>
						</datasetFetcher.Form>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6">
			<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
				<div>
					<h1 className="text-2xl font-semibold text-foreground">
						Test Data Manager
					</h1>
					<p className="text-sm text-muted-foreground">
						Manage primary and adversarial datasets, seed entries, and review
						deterministic prompts used in test runs.
					</p>
				</div>
				<div className="flex gap-2">
					<datasetFetcher.Form
						method="post"
						className="flex gap-2"
						action="/workspace/test-data"
					>
						<input type="hidden" name="intent" value="createDataset" />
						<Input
							name="name"
							placeholder="New dataset name"
							value={newDatasetName}
							onChange={(event) => setNewDatasetName(event.target.value)}
							className="w-48"
						/>
						<Button
							type="submit"
							disabled={
								!newDatasetName.trim() || datasetFetcher.state !== "idle"
							}
						>
							<Plus className="h-4 w-4 mr-1" />
							Primary Dataset
						</Button>
					</datasetFetcher.Form>
					<datasetFetcher.Form
						method="post"
						className="flex gap-2"
						action="/workspace/test-data"
					>
						<input type="hidden" name="intent" value="createDataset" />
						<Select
							value={derivativeParentId}
							onValueChange={setDerivativeParentId}
						>
							<SelectTrigger className="w-48">
								<SelectValue placeholder="Parent dataset" />
							</SelectTrigger>
							<SelectContent>
								{primaryDatasets.map((dataset) => (
									<SelectItem key={dataset.id} value={dataset.id}>
										{dataset.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Input
							name="name"
							placeholder="Adversarial name"
							value={newDerivativeName}
							onChange={(event) => setNewDerivativeName(event.target.value)}
							className="w-40"
						/>
						<input type="hidden" name="parentId" value={derivativeParentId} />
						<Button
							type="submit"
							disabled={
								!newDerivativeName.trim() ||
								!derivativeParentId ||
								datasetFetcher.state !== "idle"
							}
						>
							<Plus className="h-4 w-4 mr-1" />
							Adversarial Dataset
						</Button>
					</datasetFetcher.Form>
				</div>
			</div>

			<Card>
				<CardHeader>
					<div className="flex items-center gap-2">
						<CardTitle>Dataset Selection</CardTitle>
						{selectedDataset && (
							<>
								<Badge
									variant={
										selectedDataset.dataset_kind === "behavior_examples"
											? "default"
											: "secondary"
									}
								>
									{selectedDataset.dataset_kind === "behavior_examples"
										? "Behaviors"
										: "Rules"}
								</Badge>
								<div
									className="flex items-center gap-1.5 text-muted-foreground"
									title={
										isLocked ? lockState.lockReason : "Dataset is unlocked"
									}
								>
									{isLocked ? (
										<Lock className="h-4 w-4 text-red-500" />
									) : (
										<LockOpen className="h-4 w-4" />
									)}
								</div>
							</>
						)}
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
						<div className="flex-1">
							<Select
								value={selectedDatasetId ?? ""}
								onValueChange={handleDatasetChange}
							>
								<SelectTrigger>
									<SelectValue placeholder="Choose a dataset" />
								</SelectTrigger>
								<SelectContent>
									{datasets.map((dataset) => (
										<SelectItem key={dataset.id} value={dataset.id}>
											{dataset.name} ({dataset.type})
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{selectedDataset && (
							<div className="text-sm text-muted-foreground">
								Created{" "}
								{new Date(selectedDataset.created_at).toLocaleDateString()}
							</div>
						)}
					</div>
					{lockState.isLocked && (
						<div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
							{lockState.lockReason}
						</div>
					)}
				</CardContent>
			</Card>

			{selectedDataset && (
				<Card>
					<CardHeader>
						<CardTitle>Entries ({pagination.totalCount})</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<entryFetcher.Form
							method="post"
							className="grid gap-3"
							action="/workspace/test-data"
						>
							<input type="hidden" name="intent" value="createEntry" />
							<input
								type="hidden"
								name="datasetId"
								value={selectedDataset.id}
							/>

							{selectedDataset.dataset_kind === "operational_rules" ? (
								// For operational rules datasets - rule text and expected output
								<>
									<div className="grid gap-2">
										<label className="text-sm font-medium">Rule Text</label>
										<Textarea
											name="ruleText"
											placeholder="Enter operational rule (e.g., 'The agent should never offer discounts over 20%')"
											value={newEntryInput}
											onChange={(event) => setNewEntryInput(event.target.value)}
											disabled={isLocked}
											rows={3}
										/>
									</div>
									<div className="grid gap-2">
										<label className="text-sm font-medium">
											Expected Output
										</label>
										<Textarea
											name="expectedOutput"
											placeholder="Expected model response"
											value={newEntryExpected}
											onChange={(event) =>
												setNewEntryExpected(event.target.value)
											}
											disabled={isLocked}
										/>
									</div>
								</>
							) : (
								// For behavior examples datasets
								<>
									<div className="grid gap-2">
										<label className="text-sm font-medium">Input</label>
										<Textarea
											name="input"
											placeholder="Enter prompt or test input"
											value={newEntryInput}
											onChange={(event) => setNewEntryInput(event.target.value)}
											disabled={isLocked}
										/>
									</div>
									<div className="grid gap-2">
										<label className="text-sm font-medium">
											Expected Output
										</label>
										<Textarea
											name="expectedOutput"
											placeholder="Expected model response"
											value={newEntryExpected}
											onChange={(event) =>
												setNewEntryExpected(event.target.value)
											}
											disabled={isLocked}
										/>
									</div>
								</>
							)}

							{selectedDataset.type === "adversarial" && (
								<div className="grid gap-2">
									<label className="text-sm font-medium">Parent entry</label>
									<Select
										value={selectedParentEntryId}
										onValueChange={setSelectedParentEntryId}
										disabled={currentParentEntries.length === 0 || isLocked}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select parent entry" />
										</SelectTrigger>
										<SelectContent>
											{currentParentEntries.map((parentEntry) => (
												<SelectItem key={parentEntry.id} value={parentEntry.id}>
													{parentEntry.input.slice(0, 50)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<input
										type="hidden"
										name="parentEntryId"
										value={selectedParentEntryId}
									/>
								</div>
							)}
							<div className="flex justify-end">
								<Button
									type="submit"
									disabled={
										isLocked ||
										!newEntryInput.trim() ||
										(selectedDataset.dataset_kind === "behavior_examples" &&
											!newEntryExpected.trim()) ||
										(selectedDataset.dataset_kind === "operational_rules" &&
											!newEntryExpected.trim()) ||
										(selectedDataset.type === "adversarial" &&
											!selectedParentEntryId) ||
										entryFetcher.state !== "idle"
									}
								>
									{entryFetcher.state !== "idle" ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										"Add Entry"
									)}
								</Button>
							</div>
						</entryFetcher.Form>

						<div className="border rounded-md">
							<Table>
								<TableHeader>
									<TableRow>
										{selectedDataset.dataset_kind === "operational_rules" && (
											<TableHead>Rule Text</TableHead>
										)}
										{selectedDataset.dataset_kind === "behavior_examples" && (
											<TableHead>Input</TableHead>
										)}
										<TableHead>Expected Output</TableHead>
										<TableHead className="w-24 text-right">Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{entries.map((entry) => (
										<TableRow key={entry.id}>
											{selectedDataset.dataset_kind === "operational_rules" && (
												<TableCell className="align-top">
													<pre className="text-sm whitespace-pre-wrap break-words">
														{entry.rule_text || "N/A"}
													</pre>
												</TableCell>
											)}
											{selectedDataset.dataset_kind === "behavior_examples" && (
												<TableCell className="align-top">
													<pre className="text-sm whitespace-pre-wrap break-words">
														{entry.input}
													</pre>
												</TableCell>
											)}
											<TableCell className="align-top">
												<pre className="text-sm whitespace-pre-wrap break-words">
													{entry.expected_output}
												</pre>
											</TableCell>
											<TableCell className="text-right">
												<deleteFetcher.Form
													method="post"
													action="/workspace/test-data"
												>
													<input
														type="hidden"
														name="intent"
														value="deleteEntry"
													/>
													<input
														type="hidden"
														name="entryId"
														value={entry.id}
													/>
													<input
														type="hidden"
														name="datasetId"
														value={selectedDatasetId ?? ""}
													/>
													<Button
														type="submit"
														size="icon"
														variant="ghost"
														aria-label={`Delete entry ${entry.id}`}
														disabled={deleteFetcher.state !== "idle"}
													>
														{deleteFetcher.state !== "idle" ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<Trash2 className="h-4 w-4" />
														)}
													</Button>
												</deleteFetcher.Form>
											</TableCell>
										</TableRow>
									))}
									{entries.length === 0 && (
										<TableRow>
											<TableCell
												colSpan={3}
												className="text-center text-sm text-muted-foreground"
											>
												No entries found for this dataset.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>

						{pagination.totalPages > 1 && (
							<div className="flex items-center justify-end gap-2 text-sm">
								<span>
									Page {pagination.page} of {pagination.totalPages}
								</span>
								<Button
									variant="outline"
									size="sm"
									disabled={pagination.page === 1}
									onClick={() => handlePageChange(pagination.page - 1)}
								>
									Previous
								</Button>
								<Button
									variant="outline"
									size="sm"
									disabled={pagination.page === pagination.totalPages}
									onClick={() => handlePageChange(pagination.page + 1)}
								>
									Next
								</Button>
							</div>
						)}
					</CardContent>
				</Card>
			)}

			{selectedDataset && (
				<Card className="border-destructive/50">
					<CardHeader>
						<CardTitle className="text-destructive">Danger Zone</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="text-sm text-muted-foreground">
							Permanently delete this dataset and all its entries. This action
							cannot be undone.
						</p>
						{isLocked && (
							<p className="text-sm text-amber-600">
								This dataset cannot be deleted because it{" "}
								{lockState.lockReason.toLowerCase()}
							</p>
						)}
						{!userIsAdmin &&
							selectedDataset.user_id !==
								data.datasets.find((d) => d.id === selectedDatasetId)
									?.user_id && (
								<p className="text-sm text-amber-600">
									You do not have permission to delete this dataset (not owner
									or admin).
								</p>
							)}
						<deleteDatasetFetcher.Form
							method="post"
							action="/workspace/test-data"
						>
							<input type="hidden" name="intent" value="deleteDataset" />
							<input
								type="hidden"
								name="datasetId"
								value={selectedDataset.id}
							/>
							<Button
								type="submit"
								variant="destructive"
								disabled={
									!canDeleteDataset || deleteDatasetFetcher.state !== "idle"
								}
							>
								{deleteDatasetFetcher.state !== "idle" ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Deleting...
									</>
								) : (
									<>
										<Trash2 className="h-4 w-4 mr-2" />
										Delete Dataset
									</>
								)}
							</Button>
						</deleteDatasetFetcher.Form>
					</CardContent>
				</Card>
			)}
		</div>
	);
};

export default TestDataView;
